import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Plus, Trash2, Link2 } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import SectionsEditor from "@/components/app/SectionsEditor";
import api from "@/lib/api";

const DEFAULT_FORM = {
  slug: "",
  brand_name: "אמת או חובות",
  list_id: "",
  palette_id: "amber-charcoal",
  hero_title: "",
  hero_subtitle: "",
  pain_bullets: [],
  stats: [],
  sections: [],
  cta_primary: "אני רוצה לקחת שליטה על ההחזרים שלי",
  cta_secondary: "בואו להשתחרר מהבנקים ולהתחיל לחיות",
  webhook_url: "",
  affiliate_url: "",
  named_links: [],
  whatsapp_url: "",
  privacy_policy: "",
  terms: "",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  ga4_measurement_id: "",
  meta_pixel_id: "",
  custom_head_code: "",
  marketing_consent_text: "",
  post_signup_page: "guide",
  is_active: true,
  is_default: false,
  is_master_template: false,
};

export default function CampaignBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/${id}`);
        setForm({
          ...DEFAULT_FORM,
          ...data,
          webhook_url: data.webhook_url || "",
        });
      } catch (_) {
        toast.error("שגיאה בטעינה");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateBullet = (i, key, val) => {
    setForm((f) => {
      const bullets = [...(f.pain_bullets || [])];
      bullets[i] = { ...bullets[i], [key]: val };
      return { ...f, pain_bullets: bullets };
    });
  };
  const addBullet = () =>
    setForm((f) => ({
      ...f,
      pain_bullets: [...(f.pain_bullets || []), { title: "", text: "" }],
    }));
  const removeBullet = (i) =>
    setForm((f) => ({
      ...f,
      pain_bullets: (f.pain_bullets || []).filter((_, idx) => idx !== i),
    }));

  const updateStat = (i, key, val) => {
    setForm((f) => {
      const stats = [...(f.stats || [])];
      stats[i] = { ...stats[i], [key]: val };
      return { ...f, stats };
    });
  };
  const addStat = () =>
    setForm((f) => ({ ...f, stats: [...(f.stats || []), { value: "", label: "" }] }));
  const removeStat = (i) =>
    setForm((f) => ({ ...f, stats: (f.stats || []).filter((_, idx) => idx !== i) }));

  // ---------- named_links (multiple affiliate offers per funnel) ----------
  const slugifyKey = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32);

  const updateLink = (i, key, val) => {
    setForm((f) => {
      const links = [...(f.named_links || [])];
      const row = { ...links[i], [key]: val };
      // Auto-slugify key when the user updates label and key looks empty/derived
      if (key === "label" && (!links[i]?.key || links[i]?.key === slugifyKey(links[i]?.label || ""))) {
        row.key = slugifyKey(val);
      }
      links[i] = row;
      return { ...f, named_links: links };
    });
  };
  const addLink = () =>
    setForm((f) => ({
      ...f,
      named_links: [...(f.named_links || []), { key: "", label: "", url: "" }],
    }));
  const removeLink = (i) =>
    setForm((f) => ({
      ...f,
      named_links: (f.named_links || []).filter((_, idx) => idx !== i),
    }));

  const save = async () => {
    if (!form.slug || !form.list_id || !form.hero_title || !form.affiliate_url) {
      toast.error("יש למלא slug, list_id, כותרת ראשית, וקישור אפיליאייט");
      return;
    }
    setSaving(true);
    try {
      // Filter out empty rows client-side (backend also normalizes).
      const cleanedLinks = (form.named_links || [])
        .map((l) => ({
          key: slugifyKey(l.key || l.label),
          label: (l.label || "").trim(),
          url: (l.url || "").trim(),
        }))
        .filter((l) => l.key && l.url);
      // Detect duplicate keys — keep first, warn user
      const seen = new Set();
      const dedupLinks = [];
      let dupFound = false;
      for (const l of cleanedLinks) {
        if (seen.has(l.key)) { dupFound = true; continue; }
        seen.add(l.key);
        dedupLinks.push(l);
      }
      if (dupFound) toast.warning("מזהי לינק כפולים סוננו — נשמר הראשון מכל מזהה");
      const payload = { ...form, named_links: dedupLinks, webhook_url: form.webhook_url || null };
      if (isEdit) {
        await api.put(`/campaigns/${id}`, payload);
        toast.success("נשמר בהצלחה");
      } else {
        await api.post("/campaigns", payload);
        toast.success("הקמפיין נוצר!");
        navigate("/admin/campaigns");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><p>טוען...</p></AdminLayout>;

  return (
    <AdminLayout title={isEdit ? "עריכת קמפיין" : "קמפיין חדש"}>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basics */}
          <Section title="פרטים בסיסיים">
            <Field label="Slug (URL)">
              <Input
                value={form.slug}
                data-testid="form-slug"
                onChange={(e) => set("slug", e.target.value)}
                placeholder="emet-fb-jan"
                className="h-11"
              />
            </Field>
            <Field label="שם מותג">
              <Input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="h-11" />
            </Field>
            <Field label="List ID (ניתוב)">
              <Input
                value={form.list_id}
                data-testid="form-list-id"
                onChange={(e) => set("list_id", e.target.value)}
                placeholder="emet_default"
                className="h-11"
              />
            </Field>
            <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
              <Label className="font-bold">קמפיין פעיל</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="switch-active" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
              <Label className="font-bold">קמפיין ברירת מחדל (יוצג ב-/)</Label>
              <Switch checked={form.is_default} onCheckedChange={(v) => set("is_default", v)} data-testid="switch-default" />
            </div>
            <div className="flex items-start justify-between p-3 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex-1">
                <Label className="font-bold flex items-center gap-1.5">משפך מאסטר <span className="text-brand-amber text-xs">★</span></Label>
                <p className="text-[11px] text-stone-600 mt-0.5 leading-[1.5]">
                  אפיליאייטים יוכלו לבחור בקמפיין זה כמשפך שלהם. עדכונים כאן משפיעים אוטומטית עליהם — ללא שכפולים.
                </p>
              </div>
              <Switch checked={form.is_master_template} onCheckedChange={(v) => set("is_master_template", v)} data-testid="switch-master-template" />
            </div>
          </Section>

          <Section title="תוכן Hero">
            <Field label="כותרת ראשית">
              <Textarea value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="form-hero-title" rows={2} />
            </Field>
            <Field label="תת-כותרת">
              <Textarea value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} rows={4} />
            </Field>
            <Field label="טקסט CTA ראשי">
              <Input value={form.cta_primary} onChange={(e) => set("cta_primary", e.target.value)} className="h-11" />
            </Field>
            <Field label="טקסט CTA תחתון">
              <Input value={form.cta_secondary} onChange={(e) => set("cta_secondary", e.target.value)} className="h-11" />
            </Field>
          </Section>

          <Section title="סקשנים של דף הנחיתה — סדר, הצגה ועריכת טקסטים">
            <SectionsEditor value={form.sections} onChange={(v) => set("sections", v)} />
          </Section>

          <Section title="בולטי כאב">
            {(form.pain_bullets || []).map((b, i) => (
              <div key={`bullet-${i}`} className="bg-stone-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-bold">בולט #{i + 1}</span>
                  <button onClick={() => removeBullet(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Input value={b.title} onChange={(e) => updateBullet(i, "title", e.target.value)} placeholder="כותרת" className="h-10" />
                <Textarea value={b.text} onChange={(e) => updateBullet(i, "text", e.target.value)} placeholder="טקסט" rows={2} />
              </div>
            ))}
            <Button onClick={addBullet} variant="outline" className="w-full h-10 rounded-xl"><Plus className="w-4 h-4 me-2" />הוסף בולט</Button>
          </Section>

          <Section title="נתונים / סטטיסטיקות">
            {(form.stats || []).map((s, i) => (
              <div key={`stat-${i}`} className="grid grid-cols-12 gap-2 bg-stone-50 p-3 rounded-xl">
                <Input value={s.value} onChange={(e) => updateStat(i, "value", e.target.value)} placeholder="₪8,000" className="h-10 col-span-4" />
                <Input value={s.label} onChange={(e) => updateStat(i, "label", e.target.value)} placeholder="חיסכון חודשי" className="h-10 col-span-7" />
                <button onClick={() => removeStat(i)} className="text-red-500 col-span-1"><Trash2 className="w-4 h-4 mx-auto" /></button>
              </div>
            ))}
            <Button onClick={addStat} variant="outline" className="w-full h-10 rounded-xl"><Plus className="w-4 h-4 me-2" />הוסף נתון</Button>
          </Section>

          <Section title="אינטגרציה וניתוב">
            <Field label="Webhook URL (CRM/דיוור)">
              <Input
                value={form.webhook_url}
                data-testid="form-webhook"
                onChange={(e) => set("webhook_url", e.target.value)}
                placeholder="https://api.your-crm.com/leads"
                className="h-11 ltr text-left"
              />
              <p className="text-xs text-stone-500 mt-1">בכל הגשת ליד תופעל קריאת POST לכתובת זו עם פרטי הליד ו-UTM.</p>
            </Field>
            <Field label="Affiliate URL (קישור סופי במשפך)">
              <Input
                value={form.affiliate_url}
                data-testid="form-affiliate"
                onChange={(e) => set("affiliate_url", e.target.value)}
                placeholder="https://your-questionnaire.com"
                className="h-11 ltr text-left"
              />
            </Field>
            <Field label="WhatsApp Community URL">
              <Input
                value={form.whatsapp_url}
                onChange={(e) => set("whatsapp_url", e.target.value)}
                placeholder="https://chat.whatsapp.com/..."
                className="h-11 ltr text-left"
              />
            </Field>
            <Field label="דף תודה אחרי הרשמה (Post-Signup Page)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="form-post-signup-page">
                <button
                  type="button"
                  onClick={() => set("post_signup_page", "guide")}
                  data-testid="post-signup-guide"
                  className={`text-right rounded-xl border-2 px-4 py-3 transition-colors ${form.post_signup_page === "guide" ? "border-brand-amber bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                >
                  <div className="font-bold text-sm">מדריך (ברירת מחדל)</div>
                  <div className="text-xs text-stone-500 mt-1 leading-[1.5]">
                    משפך 4 שלבים קלאסי (FunnelGuide1..4)
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => set("post_signup_page", "questionnaire")}
                  data-testid="post-signup-questionnaire"
                  className={`text-right rounded-xl border-2 px-4 py-3 transition-colors ${form.post_signup_page === "questionnaire" ? "border-brand-amber bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                >
                  <div className="font-bold text-sm">שאלון פיננסי אינטראקטיבי</div>
                  <div className="text-xs text-stone-500 mt-1 leading-[1.5]">
                    מבדק יחס החזרים + CTA לשיחת ייעוץ/וואטסאפ
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => set("post_signup_page", "affiliate_pitch")}
                  data-testid="post-signup-affiliate-pitch"
                  className={`text-right rounded-xl border-2 px-4 py-3 transition-colors ${form.post_signup_page === "affiliate_pitch" ? "border-brand-amber bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                >
                  <div className="font-bold text-sm">פיץ׳ לאיחוד הלוואות (רנטגן התזרים)</div>
                  <div className="text-xs text-stone-500 mt-1 leading-[1.5]">
                    עמוד גלילה עם 8 סקשנים + CTA לאחר כל סקשן ל-Affiliate URL
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => set("post_signup_page", "affiliate_redirect")}
                  data-testid="post-signup-affiliate-redirect"
                  className={`text-right rounded-xl border-2 px-4 py-3 transition-colors ${form.post_signup_page === "affiliate_redirect" ? "border-brand-amber bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                >
                  <div className="font-bold text-sm">הפניה ישירה ללינק האפיליאייט</div>
                  <div className="text-xs text-stone-500 mt-1 leading-[1.5]">
                    הליד נשמר, ואז הפניה חיצונית ל-Affiliate URL עם UTMs
                  </div>
                </button>
              </div>
              {(form.post_signup_page === "affiliate_redirect" || form.post_signup_page === "affiliate_pitch") && !form.affiliate_url && (
                <p className="text-xs text-amber-700 mt-2 leading-[1.6] font-semibold">
                  {`⚠ שים לב: נבחרה הפניה ללינק אפיליאייט אבל שדה "Affiliate URL" ריק. הליד יישמר, אבל ה-CTAs לא יקושרו לאף מקום.`}
                </p>
              )}
              <p className="text-xs text-stone-500 mt-2 leading-[1.6]">
                בוחר איזה דף מוצג אחרי שהגולש משאיר פרטים בטופס. ניתן להוסיף בהמשך תבניות נוספות לספריית "דפי התודה".
              </p>
            </Field>
          </Section>

          <Section title={<span className="inline-flex items-center gap-2"><Link2 className="w-4 h-4 text-brand-amber" />לינקי אפיליאייט נוספים (הצעות ל־{`{{link:KEY}}`})</span>}>
            <p className="text-xs text-stone-600 leading-[1.7] -mt-1">
              לינקים נוספים המסונפים ל-funnel הזה. כל לינק מקבל <b>שם תצוגה</b> (למשל &quot;הצעה עיקרית&quot;) ו<b>מזהה קצר</b> (למשל <code className="bg-stone-100 px-1.5 py-0.5 rounded ltr text-[10px]">main</code>).
              בעורך המייל תוכל להכניס <code className="bg-stone-100 px-1.5 py-0.5 rounded ltr text-[10px]">{`{{link:main}}`}</code> — בזמן שליחה המערכת תחליף את זה בכתובת המתאימה ל-funnel של הליד, ותוסיף אליה את כל ה-sub1..sub10 (מעקב מלא).
              אם ליד הגיע מ-funnel שאין בו את המזהה המבוקש, המערכת תעטוף את הלינק בכתובת ברירת המחדל (Affiliate URL למעלה).
            </p>
            {(form.named_links || []).length === 0 && (
              <div className="p-4 rounded-xl bg-stone-50 text-center text-xs text-stone-500 border border-dashed border-stone-300">
                אין לינקים נוספים ל-funnel זה. הוסף לינק כדי לאפשר להכניס אותו לפי שם ממנוע המיילים.
              </div>
            )}
            {(form.named_links || []).map((l, i) => (
              <div key={`link-${i}`} className="bg-stone-50 rounded-xl p-3 space-y-2" data-testid={`named-link-row-${i}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-stone-500">לינק #{i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`named-link-remove-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 sm:col-span-5">
                    <Label className="text-[11px] font-bold text-stone-600 mb-1 block">שם תצוגה</Label>
                    <Input
                      value={l.label || ""}
                      onChange={(e) => updateLink(i, "label", e.target.value)}
                      placeholder="הצעה עיקרית"
                      data-testid={`named-link-label-${i}`}
                      className="h-10"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-3">
                    <Label className="text-[11px] font-bold text-stone-600 mb-1 block">מזהה (KEY)</Label>
                    <Input
                      value={l.key || ""}
                      onChange={(e) => updateLink(i, "key", slugifyKey(e.target.value))}
                      placeholder="main"
                      data-testid={`named-link-key-${i}`}
                      className="h-10 ltr text-left font-mono text-xs"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-4">
                    <Label className="text-[11px] font-bold text-stone-600 mb-1 block">Placeholder לשילוב במייל</Label>
                    <div className="h-10 flex items-center px-3 bg-white rounded-md border border-stone-200">
                      <code className="text-[11px] ltr font-mono text-stone-500 truncate">
                        {l.key ? `{{link:${l.key}}}` : "—"}
                      </code>
                    </div>
                  </div>
                  <div className="col-span-12">
                    <Label className="text-[11px] font-bold text-stone-600 mb-1 block">URL היעד</Label>
                    <Input
                      value={l.url || ""}
                      onChange={(e) => updateLink(i, "url", e.target.value)}
                      placeholder="https://offer.example.com/landing"
                      data-testid={`named-link-url-${i}`}
                      dir="ltr"
                      className="h-10 ltr text-left"
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              onClick={addLink}
              variant="outline"
              className="w-full h-10 rounded-xl"
              data-testid="add-named-link-btn"
            >
              <Plus className="w-4 h-4 me-2" />הוסף לינק נוסף
            </Button>
          </Section>

          <Section title="קודי מעקב ופיקסלים (קמפיין ראשי)">
            <p className="text-xs text-stone-500 -mt-2 leading-[1.6]">
              קודים אלו נטענים בכל גולש לאחר הסכמת עוגיות. GA4 דורש הסכמת "שיפור המדריכים", Meta Pixel ו-Custom Code דורשים "הזדמנויות כלכליות".
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Google Analytics 4 — Measurement ID">
                <Input
                  value={form.ga4_measurement_id}
                  onChange={(e) => set("ga4_measurement_id", e.target.value)}
                  data-testid="form-ga4"
                  placeholder="G-XXXXXXXXXX"
                  className="h-11 ltr text-left font-mono"
                />
              </Field>
              <Field label="Meta Pixel ID">
                <Input
                  value={form.meta_pixel_id}
                  onChange={(e) => set("meta_pixel_id", e.target.value)}
                  data-testid="form-meta-pixel"
                  placeholder="1234567890123456"
                  className="h-11 ltr text-left font-mono"
                />
              </Field>
            </div>
            <Field label="Custom Head Code (HTML/JS חופשי — נטען בהסכמת שיווק)">
              <Textarea
                value={form.custom_head_code}
                onChange={(e) => set("custom_head_code", e.target.value)}
                data-testid="form-custom-code"
                rows={6}
                placeholder={'<script>// Hotjar / Clarity / TikTok Pixel וכו\'</script>'}
                className="ltr text-left font-mono text-xs"
              />
            </Field>
          </Section>

          <Section title="מסמכים משפטיים ויצירת קשר">
            <p className="text-xs text-stone-500 -mt-2">
              שדות אלו מופיעים בלינקים בפוטר של דף הנחיתה והמשפך. שורה ריקה = שטח לבן. שורות שמתחילות ב-"1. " וכו' יוצגו ככותרות; שורות שמתחילות ב-"• " או "- " יוצגו כתבליטים. הטקסט החופשי יוצג כפסקאות.
            </p>
            <Field label="מדיניות פרטיות">
              <Textarea
                value={form.privacy_policy}
                onChange={(e) => set("privacy_policy", e.target.value)}
                data-testid="form-privacy"
                rows={10}
                placeholder="הזינו את מדיניות הפרטיות..."
              />
            </Field>
            <Field label="תקנון">
              <Textarea
                value={form.terms}
                onChange={(e) => set("terms", e.target.value)}
                data-testid="form-terms"
                rows={10}
                placeholder="הזינו את התקנון..."
              />
            </Field>
            <Field label="טקסט הסכמה לדיוור (מתחת לטופס)">
              <Textarea
                value={form.marketing_consent_text}
                onChange={(e) => set("marketing_consent_text", e.target.value)}
                data-testid="form-consent-text"
                rows={3}
                placeholder='אני מאשר/ת קבלת תוכן שיווקי, טיפים ועדכונים לגבי {brand} במייל וב-SMS. ניתן לבטל בכל עת.'
                dir="rtl"
              />
              <p className="text-xs text-stone-500 mt-1.5 leading-[1.6]">
                טקסט הצ'קבוקס הגלוי מתחת לטופס ההרשמה. שאירו ריק לטקסט ברירת מחדל. השתמשו במשתנה <code className="bg-stone-100 px-1.5 py-0.5 rounded ltr text-[10px]">{"{brand}"}</code> — יוחלף אוטומטית בשם המותג ("{form.brand_name || "אמת או חובות"}"). קישור למדיניות פרטיות מתווסף אוטומטית בסוף.
              </p>
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="אימייל ליצירת קשר">
                <Input
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  data-testid="form-contact-email"
                  placeholder="contact@brand.co.il"
                  className="h-11 ltr text-left"
                />
              </Field>
              <Field label="טלפון">
                <Input
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  data-testid="form-contact-phone"
                  placeholder="03-1234567"
                  className="h-11 ltr text-left"
                />
              </Field>
            </div>
            <Field label="כתובת פיזית (אופציונלי)">
              <Input
                value={form.contact_address}
                onChange={(e) => set("contact_address", e.target.value)}
                data-testid="form-contact-address"
                placeholder="רחוב הברזל 23, תל אביב"
                className="h-11"
              />
            </Field>
          </Section>

          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-campaign-btn"
            className="w-full h-14 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold text-base"
          >
            <Save className="w-4 h-4 me-2" />
            {saving ? "שומר..." : isEdit ? "שמור שינויים" : "צור קמפיין"}
          </Button>
        </div>

        {/* Preview */}
        <div className="lg:col-span-1">
          <div className="lg:sticky top-6 bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-[800] mb-3">תצוגה מקדימה</h3>
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs text-stone-500 mb-1">כתובת הדף:</div>
              <div className="font-bold text-xs ltr break-all">/c/{form.slug || "—"}</div>
              <hr className="my-3 border-stone-200" />
              <div className="text-xs text-stone-500 mb-1">כותרת:</div>
              <div className="font-[800] text-sm leading-snug mb-3">{form.hero_title || "—"}</div>
              <div className="text-xs text-stone-500 mb-1">תת-כותרת:</div>
              <p className="text-xs text-stone-600 mb-3 line-clamp-4">{form.hero_subtitle || "—"}</p>
              <div className="text-xs text-stone-500 mb-1">List → Affiliate:</div>
              <div className="text-xs font-bold">{form.list_id || "—"}</div>
              <div className="text-xs ltr break-all text-brand-amber">{form.affiliate_url || "—"}</div>
            </div>
            {form.slug && (
              <a
                href={`/c/${form.slug}`}
                target="_blank"
                rel="noreferrer"
                className="block mt-4 text-center text-sm font-bold text-brand-amber hover:underline"
              >
                פתח דף בחלון חדש →
              </a>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-stone-200 space-y-4">
      <h2 className="font-[800] text-lg">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-bold mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
